'use client';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { ButtonLink } from '@commons/components/Button';
import IconButton from '@commons/components/IconButton';
import Link from '@commons/components/Link';
import LocaleSwitcher from '@commons/components/LocaleSwitcher';
import MegaMenu from '@commons/components/PageLayout/Header/MegaMenu';
import Text from '@commons/components/Text';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import type { Alternates } from '@commons/i18n';
import PageBuilder from '@commons/pagebuilder';

import type { MainMenuLink } from '..';
import styles from './MainMenu.module.scss';

// Type definition for the MainMenu component properties.

type Props = {
  menu: MainMenuLink[];
  alternates: Alternates;
  className?: string;
};

/**
 * Desktop Main Menu component.
 */

const MainMenu = ({ menu, alternates, className }: Props) => {
  const t = useTranslations('commons');
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MainMenuLink | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<MainMenuLink | null>(null);

  const hasSublinks = activeMenu?.subLinks;
  const hasthirdlinks = activeSubMenu?.subLinks;

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  // Handles hover over main menu item.
  const handleMenuEnter = (menuItem: MainMenuLink) => {
    setActiveMenu(menuItem);
    setActiveSubMenu(null); // Reset submenu on new main menu hover.
  };

  // Handles hover over submenu item to show the third-level menu.
  const handleSubMenuEnter = (subMenuItem: MainMenuLink) => {
    setActiveSubMenu(subMenuItem);
  };

  // Handle mouse leave for the entire menu (resets the submenu and third menu).
  const handleMenuLeave = () => {
    setActiveMenu(null);
    setActiveSubMenu(null);
  };

  const renderMainMenu = () => (
    <NavigationMenu.List className={styles.menuColumn}>
      {menu.map((link: MainMenuLink) => {
        if (link.isButton) return null;
        return (
          <NavigationMenu.Item key={link.uuid}>
            <NavigationMenu.Trigger
              className={clsx(styles.menuItem, link.uuid === activeMenu?.uuid ? styles.active : '')}
              onMouseEnter={() => handleMenuEnter(link)}
            >
              {link.subLinks ? (
                <Text
                  as="h3"
                  marginBottom="sm"
                  className={clsx(styles.menuLink, link.class, link.isActive && styles.activeLink)}
                >
                  {link.title}
                </Text>
              ) : (
                <NavigationMenu.Link asChild>
                  <Link
                    level="h3"
                    href={link.url}
                    active={link.isActive}
                    className={clsx(
                      styles.menuLink,
                      link.class,
                      link.isActive && styles.activeLink
                    )}
                    onClick={toggleMenu}
                  >
                    {link.title}
                  </Link>
                </NavigationMenu.Link>
              )}
            </NavigationMenu.Trigger>
          </NavigationMenu.Item>
        );
      })}
    </NavigationMenu.List>
  );

  const renderSubMenu = () => {
    if (!activeMenu || !activeMenu.subLinks) return null;

    const hasButton = activeMenu.url && activeMenu.url !== '#' && activeMenu.buttonLabel;

    return (
      <NavigationMenu.List className={styles.subMenu}>
        <BaseContainer
          border={activeMenu?.url ? 'bottom' : undefined}
          marginBottom="md"
          paddingBottom="md"
        >
          {activeMenu.subLinks.map((subLink: MainMenuLink) => (
            <NavigationMenu.Item key={subLink.uuid} className={styles.subMenuItem}>
              <NavigationMenu.Trigger
                className={clsx(
                  styles.menuItem,
                  subLink.uuid === activeSubMenu?.uuid ? styles.active : ''
                )}
                onMouseEnter={() => handleSubMenuEnter(subLink)}
              >
                {!subLink.url ? (
                  <Text
                    level="h5"
                    marginBottom="none"
                    className={clsx(
                      styles.link,
                      subLink.class,
                      subLink.isActive && styles.activeLink
                    )}
                  >
                    {subLink.title}
                  </Text>
                ) : (
                  <NavigationMenu.Link asChild>
                    <Link
                      href={subLink.url}
                      level="h5"
                      weight="normal"
                      onClick={toggleMenu}
                      className={clsx(
                        styles.link,
                        styles.clickable,
                        subLink.class,
                        subLink.isActive && styles.activeLink
                      )}
                    >
                      {subLink.title}
                    </Link>
                  </NavigationMenu.Link>
                )}
                {subLink.description && (
                  <Text marginTop="xxs" marginBottom="none">
                    {subLink.description}
                  </Text>
                )}
              </NavigationMenu.Trigger>
            </NavigationMenu.Item>
          ))}
        </BaseContainer>
        {hasButton && (
          <NavigationMenu.Link asChild>
            <ButtonLink variant="primary" href={activeMenu.url}>
              {activeMenu.buttonLabel}
            </ButtonLink>
          </NavigationMenu.Link>
        )}
      </NavigationMenu.List>
    );
  };

  const renderThirdMenu = () => {
    if (activeMenu && activeMenu.contentReference) {
      return (
        <NavigationMenu.Link asChild>
          <BaseContainer className={styles.insightsCard}>
            {PageBuilder.renderNode(activeMenu.contentReference)}
          </BaseContainer>
        </NavigationMenu.Link>
      );
    }

    if (!activeSubMenu || !activeSubMenu.subLinks) return null;

    return (
      <NavigationMenu.List className={styles.thirdMenu}>
        {activeSubMenu.subLinks.map((thirdLink: MainMenuLink) => (
          <NavigationMenu.Item key={thirdLink.uuid} className={styles.subMenuItem}>
            {!thirdLink.url ? (
              <Text
                level="h5"
                weight="normal"
                marginBottom="none"
                className={clsx(
                  styles.link,
                  thirdLink.class,
                  thirdLink.isActive && styles.activeLink
                )}
              >
                {thirdLink.title}
              </Text>
            ) : (
              <NavigationMenu.Link asChild>
                <Link
                  level="h5"
                  weight="normal"
                  href={thirdLink.url}
                  onClick={toggleMenu}
                  withArrow={!thirdLink.url ? undefined : 'md'}
                  className={clsx(
                    styles.link,
                    styles.clickable,
                    thirdLink.class,
                    thirdLink.isActive && styles.activeLink
                  )}
                >
                  {thirdLink.title}
                </Link>
              </NavigationMenu.Link>
            )}
            {thirdLink.description && (
              <Text marginTop="xxs" marginBottom="none" className={styles.description}>
                {thirdLink.description}
              </Text>
            )}
          </NavigationMenu.Item>
        ))}
      </NavigationMenu.List>
    );
  };

  const renderedTrigger = (
    <IconButton
      type={isOpen ? 'close' : 'menu'}
      active={isOpen}
      colors={{ icon: 'black', bg: 'grey-1' }}
      activeColors={{ icon: 'black', bg: 'grey-1' }}
      onClick={toggleMenu}
      className={clsx(styles.iconTrigger, isOpen && styles.open, 'nav_ham_icon')}
      aria-label={t(isOpen ? 'close' : 'open')}
    />
  );

  const lastMenuItem = menu[menu.length - 1];
  const hasButtonInMainMenu = lastMenuItem?.isButton;
  const renderedMainMenuButton = hasButtonInMainMenu ? (
    <ButtonLink variant="secondary" href={lastMenuItem.url}>
      {lastMenuItem.title}
    </ButtonLink>
  ) : null;

  const classes = clsx(styles.mainMenu, isOpen && 'activeDesktopMenu', className);

  return (
    <MegaMenu
      theme="white"
      trigger={renderedTrigger}
      triggerMode="click"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      className={classes}
      containerClassName={styles.menuContent}
    >
      <NavigationMenu.Root>
        <Flex
          gap="none"
          width={12}
          direction="row"
          className={clsx(styles.wrapper, 'h-100')}
          onMouseLeave={handleMenuLeave}
        >
          <Flex
            width={4}
            border={activeMenu !== null && hasSublinks ? 'right' : undefined}
            justify="space-between"
            direction="column"
            paddingRight="md"
            marginRight="md"
            className={styles.menuColumn}
          >
            {renderMainMenu()}
            <Flex gap="lg" direction="column">
              {renderedMainMenuButton}
              <LocaleSwitcher alternates={alternates} />
            </Flex>
          </Flex>
          <Flex.Item
            width={4}
            border={activeSubMenu !== null && hasthirdlinks ? 'right' : undefined}
            paddingRight="md"
            marginRight="md"
            className={clsx(styles.menuColumn, 'h-100')}
          >
            {renderSubMenu()}
          </Flex.Item>
          <Flex.Item width={4} className={clsx(styles.menuColumn, 'h-100')}>
            {renderThirdMenu()}
          </Flex.Item>
        </Flex>
      </NavigationMenu.Root>
    </MegaMenu>
  );
};

export default MainMenu;
